// Unit markers with screen-space spiderfying + collision relaxation.
// Split out of DispatchMap.jsx (refactor only — logic unchanged).
import React, { useState } from "react";
import { Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { DISC } from "@/components/badges";
import { gpsState, ageLabel, unitIcon, ACTIVE_UNIT } from "./mapShared";

// Screen-space spiderfying. Units whose ICONS would overlap at the current zoom
// (pixel distance < CLUSTER_PX) are grouped and fanned out on a Leaflet-style
// circle/spiral scaled for the 42px icons, so every marker clears its neighbours
// and stays individually clickable. Recomputes on every zoom change.
const _2PI = Math.PI * 2;
const _M = 2.0; // distance multiplier tuned for the 42px unit icons
function circlePts(count) {
  const circumference = _M * 25 * (2 + count);
  const legLength = Math.max(circumference / _2PI, 48);
  const step = _2PI / count;
  const res = [];
  for (let i = 0; i < count; i++) res.push([legLength * Math.cos(i * step), legLength * Math.sin(i * step)]);
  return res;
}
function spiralPts(count) {
  let legLength = _M * 14, angle = 0;
  const separation = _M * 28, lengthFactor = _M * 7 * _2PI;
  const res = new Array(count);
  for (let i = count - 1; i >= 0; i--) {
    angle += separation / legLength + i * 0.0005;
    res[i] = [legLength * Math.cos(angle), legLength * Math.sin(angle)];
    legLength += (_2PI * lengthFactor) / angle;
  }
  return res;
}

export function UnitLayer({ units, onTrail }) {
  const map = useMap();
  const [, setTick] = useState(0);
  useMapEvents({ zoomend: () => setTick((t) => t + 1), zoomlevelschange: () => setTick((t) => t + 1) });

  const CLUSTER_PX = 52;
  const clusters = [];
  units.forEach((u) => {
    const pt = map.latLngToLayerPoint([u.lat, u.lng]);
    let cl = clusters.find((c) => c.anchor.distanceTo(pt) < CLUSTER_PX);
    if (!cl) { cl = { anchor: pt, items: [] }; clusters.push(cl); }
    cl.items.push({ u, pt });
  });

  // Build layer-pixel nodes: singletons at their own point, clusters spiderfied.
  const nodes = [];
  clusters.forEach((cl) => {
    if (cl.items.length === 1) {
      const it = cl.items[0];
      nodes.push({ u: it.u, x: it.pt.x, y: it.pt.y });
      return;
    }
    const n = cl.items.length;
    const offs = n < 9 ? circlePts(n) : spiralPts(n);
    cl.items.forEach((it, i) => nodes.push({ u: it.u, x: cl.anchor.x + offs[i][0], y: cl.anchor.y + offs[i][1] }));
  });

  // Collision-relaxation: guarantee every icon is >= MIN_PX from all others at the
  // current zoom, so no 42px marker can overlap another regardless of clustering.
  const MIN_PX = 44;
  for (let iter = 0; iter < 12; iter++) {
    let moved = false;
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        let dx = nodes[j].x - nodes[i].x, dy = nodes[j].y - nodes[i].y;
        const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
        if (d < MIN_PX) {
          const push = (MIN_PX - d) / 2, ux = dx / d, uy = dy / d;
          nodes[i].x -= ux * push; nodes[i].y -= uy * push;
          nodes[j].x += ux * push; nodes[j].y += uy * push;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }

  const placed = nodes.map((nd) => {
    const ll = map.layerPointToLatLng(L.point(nd.x, nd.y));
    return { u: nd.u, lat: ll.lat, lng: ll.lng };
  });

  return placed.map(({ u, lat, lng }) => {
    const c = (DISC[u.type] || {}).color || "#9CA3AF";
    return (
      <Marker key={u.unit_id} position={[lat, lng]} icon={unitIcon(c, u.type, ACTIVE_UNIT.has(u.status))}>
          <Popup>
            <div className="font-mono text-xs" data-testid={`unit-popup-${u.unit_id}`}>
              <b>{u.unit_id}</b> — {u.type.toUpperCase()}<br />
              {u.personnel}<br />
              {u.agency && <>{u.agency}<br /></>}
              Status: {u.status}<br />
              {u.current_incident ? `On: ${u.current_incident}` : "Unassigned"}<br />
              {(() => {
                const [lab, col] = gpsState(u);
                return (
                  <span>
                    <span style={{ color: col, fontWeight: 700 }}>● {lab}</span>
                    {u.gps?.ts && <> · updated {ageLabel(u.gps.ts)}</>}
                    {Number.isFinite(u.gps?.accuracy) && <> · ±{Math.round(u.gps.accuracy)} m</>}
                  </span>
                );
              })()}
              {onTrail && (
                <div style={{ marginTop: 4 }}>
                  <button data-testid={`unit-trail-${u.unit_id}`} onClick={() => onTrail(u)}
                    style={{ background: "#22D3EE", color: "#04202b", fontWeight: 700, fontSize: 10, padding: "2px 8px", borderRadius: 2, border: 0, cursor: "pointer" }}>
                    GPS TRAIL (8h)
                  </button>
                </div>
              )}
            </div>
          </Popup>
        </Marker>
    );
  });
}
